import { collection, doc, getDoc, getDocs, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { computeSeatSummary, firebaseHelper, isDesignerRole } from "ui";
import { TeamsStatus } from "ts-interfaces";
import { useMattoState } from "../MattoState";

// How long to wait for teamsChanged to clear permissions.teamsInactive.
const INACTIVE_SYNC_TIMEOUT_MS = 10_000;

// An inactive admin's way back in (the one write the Teams sharing rules leave
// them): set their own roster row active, wait for teamsChanged to drop the team
// from permissions.teamsInactive, then reload so every cached permission reads it.
// Resolves false (after saying why) when it can't.
export async function reactivateOwnMembership(uid: string, firmId: string): Promise<boolean> {
  const setSnackbarMessage = useMattoState.getState().setSnackbarMessage;
  try {
    const db = firebaseHelper.db;
    const snap = await getDocs(
      query(collection(db, "teams"), where("uid", "==", uid), where("designFirmID", "==", firmId)),
    );
    const row = snap.docs.find((d) => d.data().status === TeamsStatus.INACTIVE);
    if (!row) throw new Error("There's no paused membership to reactivate.");
    // A designer seat needs a free one, as on the Members page.
    if (isDesignerRole(row.data().role)) {
      const [firm, roster] = await Promise.all([
        getDoc(doc(db, "design_firms", firmId)),
        getDocs(query(collection(db, "teams"), where("designFirmID", "==", firmId))),
      ]);
      const seats = computeSeatSummary(roster.docs.map((d) => d.data() as any), firm.data()?.seats ?? 0);
      if (seats.availableSeats <= 0) {
        throw new Error("Your team has no free seat. Another admin can add one or free one up.");
      }
    }
    await updateDoc(row.ref, { status: TeamsStatus.ACTIVE, updatedAt: Date.now() });
    setSnackbarMessage("You're active in this team again. Reloading…");
    await new Promise<void>((resolve) => {
      let unsub = () => {};
      const done = () => {
        clearTimeout(timer);
        unsub();
        resolve();
      };
      const timer = setTimeout(done, INACTIVE_SYNC_TIMEOUT_MS);
      unsub = onSnapshot(
        doc(db, "permissions", uid),
        (p) => {
          if (!(p.data()?.teamsInactive ?? []).includes(firmId)) done();
        },
        done,
      );
    });
    window.location.reload();
    return true;
  } catch (err) {
    setSnackbarMessage(err instanceof Error ? err.message : "Couldn't reactivate. Please try again.");
    return false;
  }
}
